import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type {
  AccessibilityPrefsDTO,
  ColorSchemePref,
  ContrastMode,
  DirectionPref,
  TextScale,
} from '@/services/accessibilityPrefsApi';

/**
 * Per-user accessibility preferences. Persisted to localStorage so the
 * UI applies the right values before the post-auth /accessibility-prefs
 * fetch resolves — a doctor with large-text + dark mode never gets a
 * flash of default styling on cold boot.
 *
 * The store also carries the imperative DOM applier; useTheme +
 * useLocalizationStore consult `colorScheme` and `direction` to know
 * which palette / dir to commit. Centralising application here keeps
 * the rest of the codebase from re-deriving CSS-var math.
 */

const TEXT_SCALE_FACTORS: Record<TextScale, number> = {
  normal: 1,
  large: 1.15,
  xlarge: 1.3,
  xxlarge: 1.5,
};

export interface AccessibilityPrefs {
  textScale: TextScale;
  contrast: ContrastMode;
  reduceMotion: boolean;
  colorScheme: ColorSchemePref;
  direction: DirectionPref;
}

export const ACCESSIBILITY_DEFAULTS: AccessibilityPrefs = {
  textScale: 'normal',
  contrast: 'normal',
  reduceMotion: false,
  colorScheme: 'system',
  direction: 'auto',
};

interface AccessibilityStore {
  prefs: AccessibilityPrefs;

  setPrefs: (next: Partial<AccessibilityPrefs>) => void;
  /** Replaces the in-memory prefs without re-applying to DOM (sync hook hydrate path). */
  hydrateFromServer: (next: Partial<AccessibilityPrefsDTO>) => void;
  reset: () => void;
}

export const useAccessibilityStore = create<AccessibilityStore>()(
  persist(
    (set) => ({
      prefs: ACCESSIBILITY_DEFAULTS,

      setPrefs: (next) => {
        set((state) => {
          const merged = { ...state.prefs, ...next };
          applyAccessibilityPrefsToDOM(merged);
          return { prefs: merged };
        });
      },

      hydrateFromServer: (next) => {
        set((state) => {
          const merged: AccessibilityPrefs = {
            textScale: validTextScale(next.textScale) ?? state.prefs.textScale,
            contrast: validContrast(next.contrast) ?? state.prefs.contrast,
            reduceMotion:
              typeof next.reduceMotion === 'boolean'
                ? next.reduceMotion
                : state.prefs.reduceMotion,
            colorScheme:
              validColorScheme(next.colorScheme) ?? state.prefs.colorScheme,
            direction:
              validDirection(next.direction) ?? state.prefs.direction,
          };
          applyAccessibilityPrefsToDOM(merged);
          return { prefs: merged };
        });
      },

      reset: () => {
        applyAccessibilityPrefsToDOM(ACCESSIBILITY_DEFAULTS);
        set({ prefs: ACCESSIBILITY_DEFAULTS });
      },
    }),
    {
      name: 'accessibility-prefs-storage',
      version: 1,
      onRehydrateStorage: () => (state) => {
        if (state?.prefs) applyAccessibilityPrefsToDOM(state.prefs);
      },
    },
  ),
);

function validTextScale(v: unknown): TextScale | null {
  return v === 'normal' || v === 'large' || v === 'xlarge' || v === 'xxlarge'
    ? v
    : null;
}
function validContrast(v: unknown): ContrastMode | null {
  return v === 'normal' || v === 'high' ? v : null;
}
function validColorScheme(v: unknown): ColorSchemePref | null {
  return v === 'system' || v === 'light' || v === 'dark' ? v : null;
}
function validDirection(v: unknown): DirectionPref | null {
  return v === 'auto' || v === 'ltr' || v === 'rtl' ? v : null;
}

/**
 * Apply prefs imperatively to the document. Called on store changes
 * AND on rehydrate, so the page never paints stale defaults before the
 * Zustand hook subscribes.
 *
 * Mechanism:
 *   - --text-scale → root font-size multiplier; index.css sets html
 *     `font-size: calc(16px * var(--text-scale, 1))`.
 *   - data-contrast="high" → CSS overrides in index.css raise text /
 *     border opacity to meet WCAG AAA where palette allows.
 *   - data-reduce-motion="true" → CSS rule that strips animations and
 *     transition durations for users who can't tolerate motion. We
 *     respect both this and the OS-level prefers-reduced-motion.
 *   - data-color-scheme: drives Tailwind dark variant + tells useTheme
 *     which palette (light/dark) to commit. "system" means follow OS.
 *   - dir attribute / data-direction-override: useLocalizationStore
 *     consults this on language change so a user who set RTL can keep
 *     RTL while reading English content (or vice versa).
 *
 * Direction itself is not committed here — useLocalizationStore owns
 * the `dir` attribute because it also flips it on language change. We
 * only mirror the override into a data attr that the localization
 * store reads.
 */
export function applyAccessibilityPrefsToDOM(prefs: AccessibilityPrefs): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;

  root.style.setProperty(
    '--text-scale',
    String(TEXT_SCALE_FACTORS[prefs.textScale]),
  );
  root.dataset.contrast = prefs.contrast;
  root.dataset.reduceMotion = prefs.reduceMotion ? 'true' : 'false';
  root.dataset.colorScheme = prefs.colorScheme;
  root.dataset.directionOverride = prefs.direction;

  // Resolve the EFFECTIVE colour scheme — `system` defers to the OS via
  // prefers-color-scheme. We mirror the resolution onto the `dark`
  // class so Tailwind's dark variant works (tailwind.config.js is
  // already configured with darkMode: ['class']) and keep
  // data-effective-color-scheme as the canonical attribute everything
  // else reads.
  const effectiveDark = resolveEffectiveDarkScheme(prefs.colorScheme);
  root.dataset.effectiveColorScheme = effectiveDark ? 'dark' : 'light';
  root.classList.toggle('dark', effectiveDark);

  // Resolve the EFFECTIVE direction the user wants:
  //   - 'ltr' / 'rtl': hard override, write to root.dir directly.
  //   - 'auto': defer to language. We can't import useLocalizationStore
  //     here (circular), so we read its persisted blob from
  //     localStorage. This is what the localization store also does on
  //     boot — keeping us consistent without the import.
  //
  // Without this 'auto' branch, picking "Auto" after the user had
  // previously hard-set RTL/LTR did nothing — the override stayed on
  // <html> until they manually picked a side. Now Auto truly resets.
  if (prefs.direction === 'auto') {
    let langDir: 'ltr' | 'rtl' = 'ltr';
    try {
      const raw = localStorage.getItem('hospital-localization');
      if (raw) {
        const cfg = JSON.parse(raw) as { language?: string; direction?: string };
        if (cfg?.direction === 'rtl' || cfg?.direction === 'ltr') {
          langDir = cfg.direction;
        } else if (cfg?.language === 'ar') {
          langDir = 'rtl';
        }
      }
    } catch {
      // private mode / corrupt JSON — fall through to LTR default.
    }
    root.dir = langDir;
  } else {
    root.dir = prefs.direction;
  }
}

function resolveEffectiveDarkScheme(scheme: ColorSchemePref): boolean {
  if (scheme === 'dark') return true;
  if (scheme === 'light') return false;
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

/**
 * Subscribe to OS-level prefers-color-scheme changes when the user has
 * `colorScheme: 'system'` set. Returns an unsubscribe function. The
 * MainLayout hook calls this once at mount; pref-change events from
 * setPrefs() handle the non-system case.
 */
export function subscribeToSystemColorScheme(): () => void {
  if (typeof window === 'undefined' || !window.matchMedia) return () => {};
  const mql = window.matchMedia('(prefers-color-scheme: dark)');
  const handler = () => {
    const { prefs } = useAccessibilityStore.getState();
    if (prefs.colorScheme === 'system') {
      applyAccessibilityPrefsToDOM(prefs);
    }
  };
  mql.addEventListener('change', handler);
  return () => mql.removeEventListener('change', handler);
}
